'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase, signInWithGoogle, signOut, clearAuthCache } from '@/app/lib/supabase';
import { clearRecentSearchItems } from '@/app/lib/search/recentItems';
import { identifyUser, resetUser, trackUserLogin } from '@/app/lib/posthog';
import { clearDegreeProgressSnapshotKeys } from '@/app/services/degreeProgressStorage';
import type { User, AuthContextType } from '@/app/types/interfaces';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function clearApiCachesOnSignOut() {
  void Promise.all([
    import('@/app/services/api'),
    import('@/app/lib/search/searchOrchestrator'),
  ]).then(([{ clearAllApiCaches }, { clearSearchCaches }]) => {
    clearAllApiCaches();
    clearSearchCaches();
  });
}

function clearDashboardSnapshots() {
  for (const storageName of ['localStorage', 'sessionStorage'] as const) {
    try {
      const storage = window[storageName];
      for (const key of Object.keys(storage)) {
        if (/^scarlet-dashboard-(schedule|snapshot|summary)-v\d+:/.test(key)) storage.removeItem(key);
      }
    } catch { /* Storage can be disabled. */ }
  }
}

function clearPersonalDegreeSnapshots() {
  clearDegreeProgressSnapshotKeys();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || ''
          });
          identifyUser(session.user.id);
        }
      } catch (error) {
        console.error("Error fetching session:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        const userData = {
          id: session.user.id,
          email: session.user.email || ''
        };
        setUser(userData);
        
        // Identify user in PostHog
        identifyUser(session.user.id);
        
        // Track login events
        if (event === 'SIGNED_IN') {
          trackUserLogin('google');
        }
        
        // Persist provider token, if present, for future calendar use
        const providerToken = session.provider_token as string | null | undefined;
        const providerRefreshToken = (session as any).provider_refresh_token as string | null | undefined;
        const expiresAt = session.expires_at ? new Date(session.expires_at * 1000).toISOString() : undefined;
        if (providerToken) {
          supabase.functions.invoke('users', {
            body: { action: 'save_google_token', access_token: providerToken, refresh_token: providerRefreshToken, expires_at: expiresAt }
          }).catch(() => {});
        }
      } else {
        setUser(null);
        // Preserve anonymous activity for attribution; reset only after an actual sign-out.
        if (event === 'SIGNED_OUT') { resetUser(); clearDashboardSnapshots(); clearPersonalDegreeSnapshots(); }
        // Clear any in-memory API caches on sign-out
        clearApiCachesOnSignOut();
        clearRecentSearchItems();
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    user,
    loading,
    signIn: async (redirectPath?: string) => {
      try {
        await signInWithGoogle(redirectPath);
      } catch (error) {
        console.error('Error signing in:', error);
        throw error;
      }
    },
    signOut: async () => {
      try {
        await signOut();
        clearDashboardSnapshots();
        clearPersonalDegreeSnapshots();
        setUser(null);
        clearAuthCache();
        clearApiCachesOnSignOut();
        clearRecentSearchItems();
      } catch (error) {
        console.error('Error signing out:', error);
        throw error;
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Add this helper to get the current JWT access token
export async function getAccessToken() {
  const { data: { session } } = await supabase.auth.getSession();
  if (session && session.expires_at && session.expires_at * 1000 > Date.now()) {
    return session.access_token;
  }
  return null;
}
